import { ReactNode, useState } from 'react'
import { Text } from '@radix-ui/themes'
import { ChevronDown } from 'lucide-react'

interface Props<T> {
  items: T[]
  getKey: (item: T) => number
  /** The collapsed card: whatever identifies this row at a glance. */
  renderSummary: (item: T) => ReactNode
  /** Revealed in place when the card is tapped. */
  renderDetail: (item: T) => ReactNode
  emptyMessage: string
  /** Opens this card on first render — e.g. a deep link from somewhere else. */
  initialOpenKey?: number | null
  /** Two columns from `md` up; cards align to the top, so an open card doesn't
   *  stretch its neighbour. One column (the default) everywhere else. */
  columns?: 1 | 2
}

/**
 * The mobile stand-in for every track-manager table. Cards expand in place rather
 * than opening the desktop drawer: the drawer is a modal with no history entry, so
 * on a phone the back gesture doesn't close it — it navigates off the page
 * entirely. Nothing here to dismiss, so swiping back keeps meaning what the user
 * expects.
 *
 * Styled after the public events/tracks cards: full width, and an accent ring
 * when open.
 */
export default function ExpandableCards<T>({
  items,
  getKey,
  renderSummary,
  renderDetail,
  emptyMessage,
  initialOpenKey = null,
  columns = 1,
}: Props<T>) {
  const [openKey, setOpenKey] = useState<number | null>(initialOpenKey)

  if (items.length === 0) return <Text color="gray">{emptyMessage}</Text>

  return (
    <div className={columns === 2 ? 'grid grid-cols-1 items-start gap-3 md:grid-cols-2' : 'flex flex-col gap-3'}>
      {items.map((item) => {
        const key = getKey(item)
        const open = openKey === key

        return (
          <div
            key={key}
            className={`rounded-2xl border bg-panel shadow-sm transition ${
              open ? 'border-accent9 ring-2 ring-accent9' : 'border-gray5'
            }`}
          >
            <button
              type="button"
              onClick={() => setOpenKey(open ? null : key)}
              className="flex w-full cursor-pointer items-start gap-3 p-3.5 text-left"
              aria-expanded={open}
            >
              <div className="min-w-0 flex-1">{renderSummary(item)}</div>
              <ChevronDown
                className={`mt-1 h-4 w-4 shrink-0 text-gray9 transition-transform ${
                  open ? 'rotate-180' : ''
                }`}
              />
            </button>

            {open && <div className="border-t border-gray5 p-3.5">{renderDetail(item)}</div>}
          </div>
        )
      })}
    </div>
  )
}
